import React, { useState, useEffect, useContext } from 'react';
import { Card, Table, Button, Badge, Alert, Form, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthContext from '../../context/AuthContext';
import './Payments.css';

const Payments = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('ClientName');
  const [sortDir, setSortDir] = useState('ASC');
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('/api/payment-tracking/projects', {
        headers: {
          'x-auth-token': token
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data);
    } catch (err) {
      console.error('Error fetching projects:', err);
      toast.error('Failed to load projects with payment schedules');
    } finally {
      setIsLoading(false);
    }
  };

  // Filter and sort projects
  useEffect(() => {
    let filtered = [...projects];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        project => 
          project.ProjectName.toLowerCase().includes(term) ||
          project.ClientName.toLowerCase().includes(term) ||
          (project.ProjectStatus && project.ProjectStatus.toLowerCase().includes(term))
      );
    }
    
    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(project => {
        const received = project.ReceivedPayments;
        const total = project.TotalPayments;
        
        if (statusFilter === 'Complete') {
          return received === total;
        } else if (statusFilter === 'In Progress') {
          return received > 0 && received < total;
        } else if (statusFilter === 'Pending') {
          return received === 0;
        }
        return true;
      });
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      // Handle special sort cases
      if (sortBy === 'ReceivedAmount' || sortBy === 'TotalAmount') {
        aVal = parseFloat(a[sortBy]) || 0;
        bVal = parseFloat(b[sortBy]) || 0;
      } else if (sortBy === 'PaymentProgress') {
        aVal = a.TotalPayments > 0 ? a.ReceivedPayments / a.TotalPayments : 0;
        bVal = b.TotalPayments > 0 ? b.ReceivedPayments / b.TotalPayments : 0;
      } else {
        aVal = a[sortBy];
        bVal = b[sortBy];
      }
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      
      // String comparison
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      // Secondary sort by ProjectName when sorting by ClientName
      if (sortBy === 'ClientName' && aVal === bVal) {
        const aProject = (a.ProjectName || '').toLowerCase();
        const bProject = (b.ProjectName || '').toLowerCase();
        if (sortDir === 'ASC') {
          return aProject > bProject ? 1 : aProject < bProject ? -1 : 0;
        } else {
          return aProject < bProject ? 1 : aProject > bProject ? -1 : 0;
        }
      }
      
      if (sortDir === 'ASC') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
    
    setFilteredProjects(filtered);
  }, [searchTerm, statusFilter, projects, sortBy, sortDir]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSortChange = (column) => {
    const newSortDir = column === sortBy && sortDir === 'ASC' ? 'DESC' : 'ASC';
    setSortBy(column);
    setSortDir(newSortDir);
  };

  const getSortIndicator = (column) => {
    if (sortBy === column) {
      return sortDir === 'ASC' ? ' ↑' : ' ↓';
    }
    return '';
  };

  const getPaymentStatusBadge = (received, total) => {
    if (received === total) {
      return <Badge bg="success">Complete</Badge>;
    } else if (received > 0) {
      return <Badge bg="warning">In Progress</Badge>;
    } else {
      return <Badge bg="secondary">Pending</Badge>;
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0.00';
    return `$${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (isLoading) {
    return <div className="text-center mt-5">Loading projects...</div>;
  }

  return (
    <div className="payments-container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Payments</h2>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="row">
            <div className="col-md-8 mb-3 mb-md-0">
              <InputGroup>
                <Form.Control
                  placeholder="Search by project name or client..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>
            </div>
            <div className="col-md-4">
              <Form.Select
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="All">All Payment Statuses</option>
                <option value="Pending">Pending (No payments)</option>
                <option value="In Progress">In Progress (Partial)</option>
                <option value="Complete">Complete (Fully paid)</option>
              </Form.Select>
            </div>
          </div>
        </Card.Body>
      </Card>

      {filteredProjects.length > 0 ? (
        <Card>
          <Card.Body>
            <Table hover responsive>
              <thead>
                <tr>
                  <th 
                    onClick={() => handleSortChange('ProjectName')} 
                    style={{ cursor: 'pointer' }}
                  >
                    Project{getSortIndicator('ProjectName')}
                  </th>
                  <th 
                    onClick={() => handleSortChange('ClientName')} 
                    style={{ cursor: 'pointer' }}
                  >
                    Client{getSortIndicator('ClientName')}
                  </th>
                  <th 
                    onClick={() => handleSortChange('PaymentProgress')} 
                    style={{ cursor: 'pointer' }}
                  >
                    Payment Status{getSortIndicator('PaymentProgress')}
                  </th>
                  <th 
                    onClick={() => handleSortChange('ReceivedAmount')} 
                    style={{ cursor: 'pointer' }}
                  >
                    Amount Received{getSortIndicator('ReceivedAmount')}
                  </th>
                  <th 
                    onClick={() => handleSortChange('TotalAmount')} 
                    style={{ cursor: 'pointer' }}
                  >
                    Total Amount{getSortIndicator('TotalAmount')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map(project => (
                  <tr 
                    key={project.ProjectId} 
                    className="project-row"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/payments/${project.ProjectId}`)}
                  >
                    <td>
                      <div className="fw-bold">{project.ProjectName}</div>
                      <small className="text-muted">{project.ProjectStatus}</small>
                    </td>
                    <td>{project.ClientName}</td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        {getPaymentStatusBadge(project.ReceivedPayments, project.TotalPayments)}
                        <span className="text-muted small">
                          {project.ReceivedPayments} of {project.TotalPayments} payments received
                        </span>
                      </div>
                    </td>
                    <td className="text-success fw-bold">
                      {formatCurrency(project.ReceivedAmount)}
                    </td>
                    <td className="fw-bold">
                      {formatCurrency(project.TotalAmount)}
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/payments/${project.ProjectId}`);
                        }}
                      >
                        View Payments →
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      ) : (
        <Alert variant="info" className="text-center">
          {projects.length > 0 ? (
            <>
              <h5>No projects match your search criteria</h5>
              <p className="mb-0">Try adjusting your search or filter.</p>
            </>
          ) : (
            <>
              <h5>No projects with payment schedules found</h5>
              <p className="mb-0">
                Create payment schedules for your projects to start tracking payments here.
              </p>
            </>
          )}
        </Alert>
      )}
    </div>
  );
};

export default Payments;
